/**
 * products-api client (read-only) for the test bench. Reuses the SPA's `authFetch`, so calls go
 * through the gateway (nginx locally) — products-api has no inbound auth of its own, but does
 * enforce a CORS allowlist, so the `local.ringswap` origin must be permitted there. Raw responses
 * are mapped to bench view-models by `bench/mapping.ts`.
 *
 * Query strings are built by `bench/filters.ts`, which is where every products-api encoding quirk
 * is documented and tested. Nothing here should hand-roll a param.
 */
import type { AuthService } from "../auth/auth-service";
import { buildQuery, type PageOptions, type SpecimenKind, type WireFilters } from "../bench/filters";
import { mapGemstone, mapRingDetail, type GemstoneSummary, type RawCombinedProduct, type RingDetail } from "../bench/mapping";
import { config } from "../config";

/** products-api's `PaginationDto` envelope. `total` is the catalogue-wide match count, which the
 *  drawer shows so a tester knows how much the wire filters actually narrowed things. */
interface ListResponse {
  data?: RawCombinedProduct[];
  total?: number;
}

/** One page of results plus the catalogue-wide total for the same filters. */
export interface Page<T> {
  rows: T[];
  total: number;
}

async function getJson(service: AuthService, path: string): Promise<unknown> {
  const res = await service.authFetch(path);
  if (!res.ok) throw new Error(`products-api ${path} → HTTP ${res.status}`);
  return res.json();
}

async function list(service: AuthService, kind: SpecimenKind, filters: WireFilters, page: PageOptions): Promise<ListResponse> {
  return (await getJson(service, `/combined-product?${buildQuery(kind, filters, page)}`)) as ListResponse;
}

/**
 * List rings. products-api returns the same `CombinedProduct` shape from the list and detail
 * routes, so each row maps to a full `RingDetail` — the catalogue drawer renders a complete
 * specimen sheet from a list row without a second request.
 */
export async function listRings(service: AuthService, filters: WireFilters, page: PageOptions): Promise<Page<RingDetail>> {
  const body = await list(service, "ring", filters, page);
  return { rows: (body.data ?? []).map((p) => mapRingDetail(p, config.cloudinaryCloud)), total: body.total ?? 0 };
}

export async function listGemstones(service: AuthService, filters: WireFilters, page: PageOptions): Promise<Page<GemstoneSummary>> {
  const body = await list(service, "stone", filters, page);
  return { rows: (body.data ?? []).map((p) => mapGemstone(p, config.cloudinaryCloud)), total: body.total ?? 0 };
}

export async function getRing(service: AuthService, sku: string): Promise<RingDetail> {
  const body = (await getJson(service, `/combined-product/${encodeURIComponent(sku)}`)) as RawCombinedProduct | null;
  if (!body || !body.sku) throw new Error(`ring ${sku} not found`);
  return mapRingDetail(body, config.cloudinaryCloud);
}

export async function getGemstone(service: AuthService, id: string): Promise<GemstoneSummary> {
  const body = (await getJson(service, `/combined-product/${encodeURIComponent(id)}`)) as (RawCombinedProduct & { id?: string }) | null;
  if (!body) throw new Error(`gemstone ${id} not found`);
  return mapGemstone(body, config.cloudinaryCloud);
}
